import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import KlaroLogo from '@/components/haccp/KlaroLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState('choice'); // choice | password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async () => {
    if (!email || !password) { toast.error('Compila tutti i campi'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login-password`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Login fallito');
      }
      const data = await res.json();
      setUser(data.user);
      toast.success('Accesso effettuato');
      const needsOnboarding = data.user?.role === 'admin' && data.user?.onboarding_completed === false;
      navigate(needsOnboarding ? '/onboarding' : '/dashboard', { replace: true, state: { user: data.user } });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-center items-center p-12 relative overflow-hidden bg-brand-blue">
        <div className="relative z-10 text-center text-white">
          <KlaroLogo className="mb-8 rounded-2xl bg-white px-5 py-4 text-left shadow-lg" />
          <h1 className="font-heading text-4xl font-semibold tracking-tight mb-4">Il laboratorio, più chiaro</h1>
          <p className="text-xl text-white/90 max-w-md">Digitalizza il tuo laboratorio alimentare con l'intelligenza artificiale</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center md:hidden mb-8"><KlaroLogo /></div>

          <div className="card-base p-8 space-y-6 border-2 border-primary/10">
            <div>
              <h2 className="font-heading text-3xl font-medium text-foreground mb-2">Accedi</h2>
              <p className="text-muted-foreground">Gestisci ingredienti, ricette e tracciabilità</p>
            </div>

            {mode === 'choice' ? (
              <>
                <Button data-testid="google-login-btn" onClick={handleGoogle} className="w-full h-14 btn-primary text-base font-medium rounded-md shadow-sm">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Continua con Google
                </Button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Oppure</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <Button variant="outline" onClick={() => setMode('password')} data-testid="password-login-btn" className="w-full h-12 border-2 border-primary text-primary hover:bg-brand-blue hover:text-white">
                  Accedi con Email e Password
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                <div><Label>Email</Label><Input type="email" className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" /></div>
                <div><Label>Password</Label><Input type="password" className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()} /></div>
                <Button onClick={handlePasswordLogin} disabled={loading} className="w-full h-12 btn-primary" data-testid="login-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Accesso...</> : 'Accedi'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <a href="/forgot-password" data-testid="forgot-link" className="text-primary hover:underline">Password dimenticata?</a>
                  <Button variant="ghost" size="sm" onClick={() => setMode('choice')}>← Indietro</Button>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-[#475467]">Non hai un account? <a href="/register" className="text-primary font-semibold hover:underline" data-testid="register-link">Registra la tua azienda</a></p>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>© 2026 Klaro</span>
            <a href="/superadmin/login" data-testid="admin-link" className="hover:text-primary flex items-center gap-1 opacity-60 hover:opacity-100">
              <Shield className="w-3 h-3" /> Admin
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
